import { CartLinesUpdateEvent } from '@shopify/events';

const DEFAULT_COMMERCE = Object.freeze({
  shippingCost: '5,99 €',
  freeShippingThreshold: '55 €',
  deliveryTime: '8–18 Werktage',
  processingTime: '1–2 Werktage',
  returnPeriod: '30 Tage',
});

const q = (selector, root = document) => root.querySelector(selector);
const qa = (selector, root = document) => [...root.querySelectorAll(selector)];
const setText = (element, value) => {
  if (element && element.textContent !== value) element.textContent = value;
};

function readCommerceConfig() {
  const node = q('[data-tierchen-commerce-config]');
  if (!node) return DEFAULT_COMMERCE;
  try {
    const parsed = JSON.parse(node.textContent || '{}');
    return Object.freeze({ ...DEFAULT_COMMERCE, ...parsed });
  } catch (_) {
    return DEFAULT_COMMERCE;
  }
}

function updateCommerceCopy() {
  const commerce = readCommerceConfig();
  const homeTrust = q('.thp__trust');
  if (homeTrust) {
    const items = [...homeTrust.children];
    setText(q('strong', items[0]), commerce.returnPeriod);
    setText(q('strong', items[1]), 'Gratis Versand');
    setText(q('span', items[1]), `ab ${commerce.freeShippingThreshold} in Deutschland`);
  }

  const footerItems = qa('.tf__trust-item');
  setText(q('strong', footerItems[0]), `${commerce.returnPeriod} Rückgabe`);
  setText(q('strong', footerItems[1]), `Versand frei ab ${commerce.freeShippingThreshold}`);

  qa('.thx__faq-list details').forEach((details) => {
    const title = q('summary', details)?.textContent?.trim() || '';
    const body = q('p', details);
    if (!body) return;
    if (title.startsWith('Wie lange dauert der Versand?')) {
      setText(body, `Die Lieferzeit nach Deutschland beträgt insgesamt ${commerce.deliveryTime}. Darin sind ${commerce.processingTime} Bearbeitungszeit bereits enthalten.`);
    } else if (title.startsWith('Was kostet der Versand?')) {
      setText(body, `Der Standardversand innerhalb Deutschlands kostet ${commerce.shippingCost}. Ab einem Bestellwert von ${commerce.freeShippingThreshold} versenden wir kostenlos.`);
    } else if (title.startsWith('Kann ich meine Bestellung zurückgeben?')) {
      setText(body, `Ja. Tierchen bietet ein freiwilliges Rückgaberecht von ${commerce.returnPeriod}. Die Einzelheiten findest du auf unserer Seite „Widerruf & Rückgabe“.`);
    }
  });

  qa('.thv-modal__facts').forEach((facts) => {
    const lines = qa('p', facts);
    setText(lines[0], 'Alle Preise inkl. gesetzlicher MwSt., zzgl. Versandkosten.');
    setText(lines[1], `Standardversand Deutschland: ${commerce.shippingCost} · kostenlos ab ${commerce.freeShippingThreshold}.`);
    setText(lines[2], `Lieferzeit: ${commerce.deliveryTime}.`);
  });
}

function selectedVariantPrice(select, modal) {
  const option = select?.selectedOptions?.[0];
  if (!option) return null;
  const parts = (option.textContent || '').split('·').map((part) => part.trim()).filter(Boolean);
  const pricePart = parts.find((part, index) => index > 0 && /(?:€|EUR|\d+[.,]\d{2})/.test(part));
  return pricePart || modal?.dataset.basePrice || null;
}

function syncVariantPrices() {
  qa('[data-buy-modal]').forEach((modal) => {
    const price = q('.thv-modal__price', modal);
    const select = q('select[data-variant-select]', modal);
    if (!price || !select) return;
    if (!modal.dataset.basePrice) modal.dataset.basePrice = price.textContent.trim().replace(/^ab\s+/i, '');

    const update = () => {
      const value = selectedVariantPrice(select, modal);
      if (value) setText(price, value);
    };
    if (select.dataset.v38PriceBound !== 'true') {
      select.dataset.v38PriceBound = 'true';
      select.addEventListener('change', update);
    }
    update();
  });
}

async function bridgeLegacyCartUpdate(event) {
  if (!(event instanceof CustomEvent) || event.detail?.source !== 'tierchen-video-feed') return;

  const source = q('[data-tierchen-video-feed]') || document;
  const activeVariant = q('[data-buy-modal]:not([hidden]) [data-variant-select]');
  const variantId = activeVariant?.value;
  if (!variantId) return;

  const deferred = CartLinesUpdateEvent.createPromise();
  source.dispatchEvent(new CartLinesUpdateEvent({
    action: 'add',
    context: 'product',
    lines: [{ merchandiseId: String(variantId), quantity: 1 }],
    promise: deferred.promise,
  }));

  try {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`Cart refresh failed: ${response.status}`);
    const ajaxCart = await response.json();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
      detail: {
        items: ajaxCart.items,
        source: 'tierchen-video-feed',
        sourceId: 'tierchen-video-feed',
        itemCount: 1,
        didError: false,
      },
    });
  } catch (error) {
    deferred.reject(error);
  }
}

function trapModalFocus(event) {
  if (event.key !== 'Tab') return;
  const modal = q('[data-buy-modal]:not([hidden])');
  if (!modal) return;

  const focusables = qa('a[href], button:not([disabled]), select:not([disabled]), input:not([disabled]):not([type="hidden"]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])', modal)
    .filter((el) => !el.hasAttribute('hidden') && el.getClientRects().length > 0);
  if (!focusables.length) return;

  const first = focusables[0];
  const last = focusables[focusables.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  } else if (!modal.contains(document.activeElement)) {
    event.preventDefault();
    first.focus();
  }
}

function refreshEnhancements() {
  updateCommerceCopy();
  syncVariantPrices();
}

function boot() {
  refreshEnhancements();
  document.addEventListener('cart:updated', bridgeLegacyCartUpdate);
  document.addEventListener('keydown', trapModalFocus, true);
  document.addEventListener('shopify:section:load', refreshEnhancements);
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
else boot();
